import React, { memo } from 'react'
import SteeringWheelIcon from 'transfix-icons/build/SteeringWheelIcon'
import { createStyles, createTheme, makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { capitalize } from 'utils/capitalize'
import { IActiveShipmentFragment } from '../../graphql/ActiveShipmentFragment'
import TrackingLabel from '../TrackingLabel'

const useActiveShipmentMapMarkerStyles = makeStyles(() => {
  const theme = createTheme()
  return createStyles({
    card: {
      padding: theme.spacing(2),
    },
    text: {
      fontSize: theme.spacing(1.5),
    },
    link: {
      fontSize: theme.spacing(1.5),
      color: brandColors.skyBlue6,
      cursor: 'pointer',
    },
    bold: {
      fontWeight: 600,
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    shipmentInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    drivers: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  })
})

export interface IActiveShipmentMapMarkerProps extends IActiveShipmentFragment {}

const ActiveShipmentMapMarker = memo<IActiveShipmentMapMarkerProps>(
  ({ etaObject, marketplaceId, stops, trackingSummary, truck, drivers = [], id }) => {
    const classes = useActiveShipmentMapMarkerStyles()

    const driverNames = drivers?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    const { truckNumber } = truck || {}

    const [firstStop] = stops || []
    const lastStop = stops[stops.length - 1]
    const { city: firstStopCity, stateCode: firstStopStateCode } = firstStop || {}
    const { city: lastStopCity, stateCode: lastStopStateCode } = lastStop || {}

    return (
      <div className={classes.card}>
        <div className={classes.flex}>
          <div className={classes.text}>{marketplaceId}</div>
          <TrackingLabel etaObject={etaObject} trackingSummary={trackingSummary} />
        </div>
        <div className={classes.shipmentInfo}>
          {driverNames && (
            <div className={classes.drivers}>
              <SteeringWheelIcon color='coolGray5' />
              <div className={`${classes.text} ${classes.bold}`}>
                {driverNames.join(', ')}
                {truckNumber ? ` - ${truckNumber}` : ''}
              </div>
            </div>
          )}
          <div>
            <div className={classes.text}>
              {capitalize(firstStopCity)}, {firstStopStateCode} - {capitalize(lastStopCity)},{' '}
              {lastStopStateCode}
            </div>
          </div>
          <div>
            <div
              id={`view-details-link-${id}`}
              className={`${classes.link} ${classes.bold}`}
              role='button'
              tabIndex={0}
              aria-hidden='true'>
              View Details
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default ActiveShipmentMapMarker
